import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keeps wizard text inputs responsive while Firestore lags behind debounced saves.
 * Binding inputs directly to `lead` from onSnapshot causes dropped characters because
 * the snapshot updates after the network round-trip, not after each keystroke.
 */
export function useLocalText(
  leadId: string,
  server: string | undefined,
  fieldPath: string,
  queueField: (path: string, v: string) => void,
): readonly [string, (v: string) => void] {
  const [local, setLocal] = useState(() => server ?? '');
  const dirty = useRef(false);

  useEffect(() => {
    setLocal(server ?? '');
    dirty.current = false;
  }, [leadId]);

  useEffect(() => {
    if (dirty.current) return;
    setLocal(server ?? '');
  }, [server]);

  useEffect(() => {
    if (dirty.current && (server ?? '') === local) {
      dirty.current = false;
    }
  }, [server, local]);

  const onChange = useCallback(
    (v: string) => {
      dirty.current = true;
      setLocal(v);
      queueField(fieldPath, v);
    },
    [fieldPath, queueField],
  );

  return [local, onChange] as const;
}

/** Local text state with a custom queue handler (e.g. nested Firestore fields). */
export function useLocalTextWithCallback(
  leadId: string,
  server: string | undefined,
  onQueue: (value: string) => void,
): readonly [string, (v: string) => void, (v: string) => void] {
  const [local, setLocal] = useState(() => server ?? '');
  const dirty = useRef(false);

  useEffect(() => {
    setLocal(server ?? '');
    dirty.current = false;
  }, [leadId]);

  useEffect(() => {
    if (dirty.current) return;
    setLocal(server ?? '');
  }, [server]);

  useEffect(() => {
    if (dirty.current && (server ?? '') === local) {
      dirty.current = false;
    }
  }, [server, local]);

  const onChange = useCallback(
    (v: string) => {
      dirty.current = true;
      setLocal(v);
      onQueue(v);
    },
    [onQueue],
  );

  const resetLocal = useCallback((v: string) => {
    dirty.current = false;
    setLocal(v);
  }, []);

  return [local, onChange, resetLocal] as const;
}
